import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { HubConnectionBuilder, HubConnection, LogLevel, HubConnectionState, HttpTransportType } from '@microsoft/signalr';
import mitt, { Emitter } from 'mitt';
import { SignalREvents } from '@/types/signalr';
import { useAuth } from './auth-context';
import { logger } from '@/utils/logger';
import { signalRAuthService } from '@/services/signalr-auth.service';

// Context value type
type SignalRContextType = {
  connection: HubConnection | null;
  emitter: Emitter<SignalREvents>;
  send: (method: keyof SignalREvents, ...args: any[]) => Promise<void>;
  invoke: <T = any>(method: string, ...args: any[]) => Promise<T>;
  isConnected: boolean;
  connectionState: HubConnectionState;
  error: Error | null;
  reconnect: () => Promise<void>;
};

const SignalRContext = createContext<SignalRContextType | undefined>(undefined);

export const useSignalR = () => {
  const ctx = useContext(SignalRContext);
  if (!ctx) throw new Error('useSignalR must be used within a SignalRProvider');
  return ctx;
};

export const SignalRProvider = ({ children }: { children: ReactNode }) => {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<HubConnectionState>(HubConnectionState.Disconnected);
  const [error, setError] = useState<Error | null>(null);
  const emitterRef = useRef<Emitter<SignalREvents>>(mitt<SignalREvents>());
  const connectionRef = useRef<HubConnection | null>(null);
  const isConnectingRef = useRef(false);
  
  // Get auth context
  const auth = useAuth();
  const isAuthenticated = auth?.isAuthenticated() ?? false;
  const isLoading = auth?.isLoading ?? true;

  // Determine the correct API URL based on environment
  const getHubUrl = useCallback(() => {
    // In production, use the public API URL directly
    // if (process.env.NODE_ENV === 'production') {
    //   return 'https://api.spearfish.io/hubs/app';
    // }
    
    // In development, check for environment variables first
    const apiUrl = process.env.NEXT_PUBLIC_API_SERVER_URL ||
                   process.env.API_SERVER_URL;

    console.log('API URL:', apiUrl);
    console.log('NEXT_PUBLIC_API_SERVER_URL:', process.env.NEXT_PUBLIC_API_SERVER_URL);
    console.log('API_SERVER_URL:', process.env.API_SERVER_URL);
    
    
    if (apiUrl) {
      return `${apiUrl}/hubs/app`;
    }
    
    // Default to localhost for development
    return 'http://localhost:5067/hubs/app';
  }, []);

  // Disconnect function
  const disconnect = useCallback(async () => {
    if (connectionRef.current && connectionRef.current.state !== HubConnectionState.Disconnected) {
      try {
        await connectionRef.current.stop();
        logger.info('SignalR disconnected', 'SignalRProvider');
      } catch (err) {
        logger.error('Error stopping SignalR connection', 'SignalRProvider', err);
      }
    }
    setConnection(null);
    setIsConnected(false);
    setConnectionState(HubConnectionState.Disconnected);
    connectionRef.current = null;
  }, []);

  // Register event handlers
  const registerEventHandlers = useCallback((conn: HubConnection) => {
    // Register all known events
    const events: (keyof SignalREvents)[] = [
      'ReceiveChat',
      'ReceiveProgress',
      'ReceiveAlert',
      'CopilotAnswer',
      'CopilotStatus',
      'CopilotStreamChunk',
      'ReceiveCopilotAck',
      'ReceiveCopilotThinking',
      'ReceiveCopilotThinkingComplete',
      'ReceiveBotInitResponse',
      'ReceiveAvailableBots',
      'ReceiveBotEvent',
      'ReceiveToolCalls',
      'ReceiveToolInvoking',
      'ReceiveToolComplete',
      'ReceiveBotMessage',
      'ReceiveTranscriptMessage',
      'ReceiveTranscriptSilence',
      'ReceiveTranscriptCrosstalk',
      'ReceiveTranscriptMetrics'
    ];

    events.forEach(event => {
      conn.on(event as string, (payload: any) => {
        logger.debug(`SignalR event received: ${String(event)}`, 'SignalRProvider', payload);
        emitterRef.current.emit(event, payload);
      });
    });
  }, []);

  // Connect function
  const connect = useCallback(async () => {
    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current || connectionRef.current?.state === HubConnectionState.Connected) {
      logger.debug('SignalR already connected or connecting', 'SignalRProvider');
      return;
    }

    // Only connect if authenticated
    if (!isAuthenticated) {
      logger.info('SignalR: Not authenticated, skipping connection', 'SignalRProvider');
      return;
    }

    isConnectingRef.current = true;
    setError(null);

    try {
      const hubUrl = getHubUrl();
      logger.info(`SignalR: Connecting to ${hubUrl}`, 'SignalRProvider');

      const newConnection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          // Use JWT token instead of cookies
          accessTokenFactory: async () => {
            try {
              const token = await signalRAuthService.getToken();
              logger.debug('SignalR token obtained for connection', 'SignalRProvider');
              return token;
            } catch (_error) {
              logger.error('Failed to get SignalR token', 'SignalRProvider', _error);
              throw _error;
            }
          },
          // Remove withCredentials since we're using JWT
          headers: {
            'X-Client-Type': 'web-portal',
            'X-Client-Version': process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0'
          },
          // Specify transports (WebSockets preferred)
          transport: HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            // Force token refresh on reconnect
            signalRAuthService.forceRefresh();
            
            // Exponential backoff: 0, 2, 4, 8, 16, 30, 30, 30...
            if (retryContext.previousRetryCount === 0) return 0;
            if (retryContext.previousRetryCount === 1) return 2000;
            if (retryContext.previousRetryCount === 2) return 4000;
            if (retryContext.previousRetryCount === 3) return 8000;
            if (retryContext.previousRetryCount === 4) return 16000;
            return 30000; // Max 30 seconds
          }
        })
        .configureLogging(process.env.NODE_ENV === 'production' ? LogLevel.Warning : LogLevel.Information)
        .build();

      // Set up connection lifecycle handlers
      newConnection.onclose(async (error) => {
        logger.info('SignalR connection closed', 'SignalRProvider', error);
        setIsConnected(false);
        setConnectionState(HubConnectionState.Disconnected);
        
        if (error) {
          setError(error);
          // Check for authentication errors
          if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
            logger.error('SignalR authentication error - clearing token and disconnecting', 'SignalRProvider');
            // Clear the token so next attempt will get a fresh one
            signalRAuthService.clearToken();
            // Don't attempt to reconnect on auth errors
            await disconnect();
          }
        }
      });

      newConnection.onreconnected((connectionId) => {
        logger.info(`SignalR reconnected with ID: ${connectionId}`, 'SignalRProvider');
        setIsConnected(true);
        setConnectionState(HubConnectionState.Connected);
        setError(null);
      });

      newConnection.onreconnecting((error) => {
        logger.info('SignalR reconnecting...', 'SignalRProvider', error);
        setIsConnected(false);
        setConnectionState(HubConnectionState.Reconnecting);
        if (error) setError(error);
      });

      // Register event handlers
      registerEventHandlers(newConnection);

      // Start the connection
      await newConnection.start();
      
      connectionRef.current = newConnection;
      setConnection(newConnection);
      setIsConnected(true);
      setConnectionState(HubConnectionState.Connected);
      
      logger.info('SignalR connected successfully', 'SignalRProvider');
    } catch (err) {
      const error = err as Error;
      logger.error('SignalR connection failed', 'SignalRProvider', error);
      setError(error);
      setIsConnected(false);
      setConnectionState(HubConnectionState.Disconnected);
      
      // Check if it's an auth error
      if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        logger.error('SignalR authentication failed - clearing token', 'SignalRProvider');
        signalRAuthService.clearToken();
      } else {
        // For non-auth errors, we could implement a retry mechanism here
        logger.info('SignalR will retry via automatic reconnect', 'SignalRProvider');
      }
    } finally {
      isConnectingRef.current = false;
    }
  }, [isAuthenticated, getHubUrl, registerEventHandlers, disconnect]);

  // Reconnect function for manual reconnection
  const reconnect = useCallback(async () => {
    logger.info('Manual SignalR reconnection requested', 'SignalRProvider');
    await disconnect();
    await connect();
  }, [connect, disconnect]);

  // Effect to handle authentication state changes
  useEffect(() => {
    // Skip if auth is still loading
    if (isLoading) {
      logger.debug('Auth still loading, waiting...', 'SignalRProvider');
      return;
    }

    if (isAuthenticated && !connectionRef.current) {
      // User is authenticated and we don't have a connection - connect
      logger.info('User authenticated, establishing SignalR connection', 'SignalRProvider');
      connect();
    } else if (!isAuthenticated && connectionRef.current) {
      // User is not authenticated but we have a connection - disconnect
      logger.info('User not authenticated, disconnecting SignalR', 'SignalRProvider');
      signalRAuthService.clearToken(); // Clear any cached tokens
      disconnect();
    }
  }, [isAuthenticated, isLoading, connect, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      logger.info('SignalR provider unmounting, cleaning up', 'SignalRProvider');
      signalRAuthService.clearToken(); // Clear tokens on unmount
      disconnect();
    };
  }, [disconnect]);

  // Send/invoke helpers with better error handling
  const send = useCallback(async (method: keyof SignalREvents, ...args: any[]) => {
    if (!connectionRef.current) {
      throw new Error('SignalR connection not established');
    }
    
    if (connectionRef.current.state !== HubConnectionState.Connected) {
      throw new Error(`SignalR not connected. Current state: ${connectionRef.current.state}`);
    }
    
    try {
      await connectionRef.current.send(method as string, ...args);
    } catch (_error) {
      logger.error(`Failed to send SignalR method: ${String(method)}`, 'SignalRProvider', _error);
      throw _error;
    }
  }, []);

  const invoke = useCallback(async <T = any>(method: string, ...args: any[]): Promise<T> => {
    if (!connectionRef.current) {
      throw new Error('SignalR connection not established');
    }
    
    if (connectionRef.current.state !== HubConnectionState.Connected) {
      throw new Error(`SignalR not connected. Current state: ${connectionRef.current.state}`);
    }
    
    try {
      return await connectionRef.current.invoke<T>(method, ...args);
    } catch (_error) {
      logger.error(`Failed to invoke SignalR method: ${method}`, 'SignalRProvider', _error);
      throw _error;
    }
  }, []);

  return (
    <SignalRContext.Provider value={{
      connection,
      emitter: emitterRef.current,
      send,
      invoke,
      isConnected,
      connectionState,
      error,
      reconnect,
    }}>
      {children}
    </SignalRContext.Provider>
  );
};